import React, { useState, useMemo } from "react";
import { X, GripVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Draggable clustering board.
 * items: [{id, text, cluster, type?, created_by}]
 * onMove(itemId, newCluster), onDelete(itemId), canDelete
 * accentFor(item) -> optional tailwind class for pill
 */
export default function DragBoard({ items, onMove, onDelete, canDelete, accentFor, renderBadge }) {
  const [dragId, setDragId] = useState(null);
  const [overCluster, setOverCluster] = useState(null);

  const clusters = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const c = it.cluster || "Overig";
      (map[c] = map[c] || []).push(it);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [items]);

  const handleDrop = (cluster) => {
    if (dragId) onMove(dragId, cluster);
    setDragId(null);
    setOverCluster(null);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="drag-board">
      {clusters.map(([cluster, list]) => (
        <div
          key={cluster}
          onDragOver={(e) => { e.preventDefault(); setOverCluster(cluster); }}
          onDragLeave={() => setOverCluster((c) => (c === cluster ? null : c))}
          onDrop={() => handleDrop(cluster)}
          className={`rounded-2xl border border-border bg-card p-4 transition-colors ${overCluster === cluster ? "drop-active" : ""}`}
          data-testid={`cluster-${cluster}`}
        >
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-display text-base font-semibold text-primary">{cluster}</h4>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground">{list.length}</span>
          </div>
          <div className="flex flex-col gap-2 min-h-[48px]">
            <AnimatePresence>
              {list.map((it) => (
                <motion.div
                  key={it.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  draggable
                  onDragStart={() => setDragId(it.id)}
                  onDragEnd={() => setDragId(null)}
                  className={`group flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm cursor-grab active:cursor-grabbing ${dragId === it.id ? "drag-ghost" : ""} ${accentFor ? accentFor(it) : "border-border bg-background"}`}
                  data-testid={`block-${it.id}`}
                >
                  <GripVertical className="h-4 w-4 shrink-0 opacity-40" />
                  <span className="flex-1 font-medium leading-snug">{it.text}</span>
                  {renderBadge && renderBadge(it)}
                  {canDelete && (
                    <button
                      onClick={() => onDelete(it.id)}
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      data-testid={`delete-${it.id}`}
                      aria-label="remove"
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      ))}
    </div>
  );
}
